import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ISingleValueSelect } from '../../utils/interfaces';
import { CARGO_DETAILS_INITIAL_STATE, CARGO_ITEM } from './initialState';
import { ICargoDetails } from './interfaces/ICargoDetails';
import { ICargoLocation } from './interfaces/ICargoLocation';
import { applyTemplate } from '../../pages/BuildShipment/reducer';
import { mapTemplateToCargoDetails } from '../../pages/BuildShipment/utils/applyTemplateMapper';

const recomputeShipmentCargoLocations = (
    pickups: Partial<ICargoLocation>[],
    dropoffs: Partial<ICargoLocation>[],
): Partial<ICargoLocation>[] => {
    const cargoLocationbyId = new Map<number, Partial<ICargoLocation>>();
    const upsert = (location?: Partial<ICargoLocation>): void => {
        if (!location || location.shipmentLocationId == null) return;
        cargoLocationbyId.set(location.shipmentLocationId, {
            ...cargoLocationbyId.get(location.shipmentLocationId),
            ...location,
        });
    };
    (pickups || []).forEach(upsert);
    (dropoffs || []).forEach(upsert);
    return Array.from(cargoLocationbyId.values());
};
const getCargoInsertIndex = (
    cargos: ICargoDetails[],
    customerId: number,
): number => {
    const lastCustomerIndex = cargos
        .map(c => c.customer?.associateId)
        .lastIndexOf(customerId);
    return lastCustomerIndex >= 0 ? lastCustomerIndex + 1 : cargos.length;
};

const findCustomerForCargo = (
    cargos: ICargoDetails[],
    customerId: number,
): ICargoDetails['customer'] => {
    return cargos.find(cargo => cargo.customer?.associateId === customerId)
        ?.customer;
};

const createCargoItem = (
    customerId: number,
    customer: ICargoDetails['customer'],
): ICargoDetails => {
    return {
        ...CARGO_ITEM,
        customer,
        customerId,
    };
};

export const cargoSlice = createSlice({
    name: 'cargo',
    initialState: CARGO_DETAILS_INITIAL_STATE,
    reducers: {
        setCargos: (state, action: PayloadAction<ICargoDetails[]>) => {
            state.cargos = action.payload;
        },
        setIsApplyRateToAllChecked: (state, action: PayloadAction<boolean>) => {
            state.isApplyRateToAllChecked = action.payload;
        },
        addCargoLine: (
            state,
            action: PayloadAction<
                | number
                | undefined
                | { customerId?: number; customer?: ICargoDetails['customer'] }
            >,
        ) => {
            const payload = action.payload as
                | number
                | undefined
                | { customerId?: number; customer?: ICargoDetails['customer'] };

            const customerId =
                typeof payload === 'number' ? payload : payload?.customerId;

            if (customerId !== undefined) {
                const insertIndex = getCargoInsertIndex(
                    state.cargos,
                    customerId,
                );

                const providedCustomer =
                    typeof payload === 'object' && payload !== null
                        ? payload.customer
                        : undefined;

                const customer =
                    providedCustomer ??
                    findCustomerForCargo(state.cargos, customerId);
                const newCargo = createCargoItem(customerId, customer);

                state.cargos.splice(insertIndex, 0, newCargo);
            } else {
                state.cargos.push(CARGO_ITEM);
            }
        },
        deleteCargoLine: (state, action: PayloadAction<number>) => {
            state.cargos = [
                ...state.cargos.filter(
                    (_, cargoIndex: number) => cargoIndex !== action.payload,
                ),
            ];
        },
        setCargoPickups: (
            state,
            action: PayloadAction<{
                index: number;
                cargoPickups: Partial<ICargoLocation>[];
            }>,
        ) => {
            const { index, cargoPickups } = action.payload;
            state.cargos[index].cargoPickups = cargoPickups;
            state.cargos[index].shipmentCargoLocations =
                recomputeShipmentCargoLocations(
                    state.cargos[index].cargoPickups,
                    state.cargos[index].cargoDropOffs,
                );
        },
        setCargoDropOffs: (
            state,
            action: PayloadAction<{
                index: number;
                cargoDropOffs: Partial<ICargoLocation>[];
            }>,
        ) => {
            const { index, cargoDropOffs } = action.payload;
            state.cargos[index].cargoDropOffs = cargoDropOffs;
            state.cargos[index].shipmentCargoLocations =
                recomputeShipmentCargoLocations(
                    state.cargos[index].cargoPickups,
                    state.cargos[index].cargoDropOffs,
                );
        },
        setCurrentActiveCargoLocation: (
            state,
            action: PayloadAction<{
                cargoIndex: number | null;
                name?: keyof Partial<ICargoLocation> | null;
            }>,
        ) => {
            state.currentActiveCargoLocation = action.payload;
        },
        setCargoDetailAtIndex: (
            state,
            action: PayloadAction<{
                cargoIndex: number;
                name: keyof ICargoDetails;
                value: boolean | number | string | ISingleValueSelect;
            }>,
        ) => {
            const { cargoIndex, name, value } = action.payload;
            state.cargos[cargoIndex] = {
                ...state.cargos[cargoIndex],
                [name]: value,
            };
        },
        setCargoDetailsAtIndex: (
            state,
            action: PayloadAction<{
                index: number;
                cargoDetails: Partial<ICargoDetails>;
            }>,
        ) => {
            const { index, cargoDetails } = action.payload;
            state.cargos[index] = {
                ...state.cargos[index],
                ...cargoDetails,
            };
        },
        setCargoPickupsAtIndex: (
            state,
            action: PayloadAction<{
                cargoIndex: number;
                pickupIndex: number;
                cargoPickup: Partial<ICargoLocation>;
            }>,
        ) => {
            const { cargoIndex, pickupIndex, cargoPickup } = action.payload;
            state.cargos[cargoIndex].cargoPickups[pickupIndex] = cargoPickup;
            state.cargos[cargoIndex].isCargoAccordionActive = true;
            state.cargos[cargoIndex].shipmentCargoLocations =
                recomputeShipmentCargoLocations(
                    state.cargos[cargoIndex].cargoPickups,
                    state.cargos[cargoIndex].cargoDropOffs,
                );
        },
        setCargoDropoffsAtIndex: (
            state,
            action: PayloadAction<{
                cargoIndex: number;
                dropOffIndex: number;
                cargoDropOff: Partial<ICargoLocation>;
            }>,
        ) => {
            const { cargoIndex, dropOffIndex, cargoDropOff } = action.payload;
            state.cargos[cargoIndex].cargoDropOffs[dropOffIndex] = cargoDropOff;
            state.cargos[cargoIndex].isCargoAccordionActive = true;
            state.cargos[cargoIndex].shipmentCargoLocations =
                recomputeShipmentCargoLocations(
                    state.cargos[cargoIndex].cargoPickups,
                    state.cargos[cargoIndex].cargoDropOffs,
                );
        },
        setEstTotalRateAtIndex: (
            state,
            action: PayloadAction<{
                index: number;
                estTotalRate: string;
            }>,
        ) => {
            const { index, estTotalRate } = action.payload;
            if (state.cargos[index]) {
                state.cargos[index].estTotalRateInUSD = estTotalRate;
            }
        },
        setActualTotalRateAtIndex: (
            state,
            action: PayloadAction<{
                index: number;
                actualTotalRate: string;
            }>,
        ) => {
            const { index, actualTotalRate } = action.payload;
            if (state.cargos[index]) {
                state.cargos[index].actualTotalRateInUSD = actualTotalRate;
            }
        },
        setEstimatedTotalRate: (state, action: PayloadAction<string>) => {
            state.estimatedTotalRate = action.payload;
        },
        setActualTotalRate: (state, action: PayloadAction<string>) => {
            state.actualTotalRate = action.payload;
        },
        resetCargoDetailsState: state => {
            state.cargos = CARGO_DETAILS_INITIAL_STATE.cargos;
            state.estimatedTotalRate =
                CARGO_DETAILS_INITIAL_STATE.estimatedTotalRate;
            state.actualTotalRate = CARGO_DETAILS_INITIAL_STATE.actualTotalRate;
            state.draftShipmentCargos =
                CARGO_DETAILS_INITIAL_STATE.draftShipmentCargos;
            state.currentActiveCargoLocation =
                CARGO_DETAILS_INITIAL_STATE.currentActiveCargoLocation;
            state.createdCommodityDetails =
                CARGO_DETAILS_INITIAL_STATE.createdCommodityDetails;
        },
        resetAssignedDriverCargoDetailsState: (
            state,
            action: PayloadAction<{ isMultiCustomer: boolean } | undefined>,
        ) => {
            const isMultiCustomer = action.payload?.isMultiCustomer;

            if (isMultiCustomer) {
                const uniqueCustomers = new Map();
                state.cargos.forEach(cargo => {
                    if (
                        cargo.customer?.associateId &&
                        !uniqueCustomers.has(cargo.customer.associateId)
                    ) {
                        uniqueCustomers.set(cargo.customer.associateId, {
                            ...CARGO_ITEM,
                            customer: cargo.customer,
                            customerId: cargo.customer?.associateId,
                        });
                    }
                });
                state.cargos = Array.from(uniqueCustomers.values());
            } else {
                state.cargos = CARGO_DETAILS_INITIAL_STATE.cargos;
            }
            state.estimatedTotalRate =
                CARGO_DETAILS_INITIAL_STATE.estimatedTotalRate;
            state.actualTotalRate = CARGO_DETAILS_INITIAL_STATE.actualTotalRate;
            state.currentActiveCargoLocation =
                CARGO_DETAILS_INITIAL_STATE.currentActiveCargoLocation;
            state.createdCommodityDetails =
                CARGO_DETAILS_INITIAL_STATE.createdCommodityDetails;
        },
        setShowCommodityModal: (state, action: PayloadAction<boolean>) => {
            state.showCommodityModal = action.payload;
        },
        setCreatedCommodityDetails: (
            state,
            action: PayloadAction<{
                id: number | null;
                index: number | null;
            }>,
        ) => {
            state.createdCommodityDetails = action.payload;
        },
        setShowReverifyRateModal: (state, action: PayloadAction<boolean>) => {
            state.showReverifyRateModal = action.payload;
        },
        setReVerifyCommodityTypes: (
            state,
            action: PayloadAction<{
                index: number;
                commodityType: string;
            }>,
        ) => {
            const { index, commodityType } = action.payload;
            state.reVerifyCommodityTypes[index] = commodityType;
        },
        setDraftShipmentCargos: (
            state,
            action: PayloadAction<ICargoDetails[]>,
        ) => {
            state.draftShipmentCargos = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(applyTemplate, (state, action) => {
            const template = action.payload;
            const cargos = mapTemplateToCargoDetails(template);
            if (cargos.length > 0) {
                state.cargos = cargos;
            }
        });
    },
});

export const {
    setCargos,
    addCargoLine,
    deleteCargoLine,
    setCargoPickups,
    setCargoDropOffs,
    setCargoDetailAtIndex,
    setCargoDetailsAtIndex,
    setCargoPickupsAtIndex,
    setCargoDropoffsAtIndex,
    setCurrentActiveCargoLocation,
    setEstTotalRateAtIndex,
    setEstimatedTotalRate,
    setActualTotalRate,
    resetCargoDetailsState,
    resetAssignedDriverCargoDetailsState,
    setShowCommodityModal,
    setCreatedCommodityDetails,
    setShowReverifyRateModal,
    setActualTotalRateAtIndex,
    setReVerifyCommodityTypes,
    setDraftShipmentCargos,
    setIsApplyRateToAllChecked,
} = cargoSlice.actions;

const CargoDetailsReducer = cargoSlice.reducer;
export default CargoDetailsReducer;
